import { useUser } from '@clerk/nextjs';
import { trpc } from '@web-app/utils/trpc';

/**
 * One hour — long enough to cover a whole session without refetching. Keep this
 * below the presigned-link TTL the walmart-signals endpoint uses, otherwise a
 * cache hit late in the window can hand over an already-dead download link.
 */
const STALE_TIME = 60 * 60 * 1000;

interface UseWalmartDocumentsParams {
  /** Healthie patient/client ID. The query stays disabled without it. */
  patientId: string | null | undefined;
  /**
   * Healthie appointment ID of the session being prepped. The endpoint requires
   * it for appointment verification, so the query stays disabled without it.
   */
  appointmentId: string | null | undefined;
  /** Feature-flag gate. When false no request is made. */
  enabled?: boolean;
}

/**
 * Fetches the Walmart PDFs stored for a patient, newest first.
 *
 * Standalone hook — works in any context. Mounting it inside the session prep
 * tab is what prefetches the list when the provider joins the session, so the
 * documents are already in the React Query cache before they open one.
 *
 * Returns an empty array when the patient has no Walmart documents.
 */
export function useWalmartDocuments({
  patientId,
  appointmentId,
  enabled = true,
}: UseWalmartDocumentsParams) {
  const { user } = useUser();

  const { data, isPending, error, refetch } =
    trpc.sessionPrep.getWalmartDocuments.useQuery(
      {
        patientId: patientId ?? '',
        // Lets the endpoint verify the request against a real appointment
        // instead of trusting the provider-patient pair alone.
        appointmentId: appointmentId ?? '',
        // viewerId keys the cache per signed-in user; the server ignores it.
        // These entries carry working presigned download links for PHI, so a
        // same-tab Clerk switch must not be served the prior provider's list.
        viewerId: user?.id ?? '',
      },
      {
        // Both ids are required by the endpoint, so don't fire without them —
        // the request would only come back as a validation error.
        enabled: enabled && !!patientId && !!appointmentId,
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    );

  return {
    documents: data ?? [],
    isLoading: enabled && !!patientId && !!appointmentId && isPending,
    error: (error as Error | null) ?? null,
    refetch,
  };
}
